import React from "react"
import ProtectedRoute from "./components/auth/ProtectedRoute"
import AdminRoute from "./components/auth/AdminRoute"
import Login from "./pages/Login"
import Dashboard from "./pages/Dashboard"
import RepairsList from "./pages/RepairsList"
import RepairDetails from "./pages/RepairDetails"
import UsersSearch from "./pages/UsersSearch"
import UserDetails from "./pages/UserDetails"
import Registration from "./pages/Registration"
import VehiclesSearch from "./pages/VehiclesSearch"
import GaragesSearch from "./pages/GaragesSearch"
import GarageDetails from "./pages/GarageDetails"
import ForgotPassword from "./pages/ForgotPassword"
import PasswordReset from "./pages/PasswordReset"

// Route configuration with additional metadata
export const routeConfig = {
  login: {
    path: "/login",
    name: "Login",
    element: <Login />,
    protected: false
  },
  registration: {
    path: "/register",
    name: "Registration",
    element: <Registration />,
    protected: false
  },
  forgotPassword: {
    path: "/forgot-password",
    name: "Forgot Password",
    element: <ForgotPassword />,
    protected: false
  },
  passwordReset: {
    path: "/password-reset/:uid",
    name: "Password Reset",
    element: <PasswordReset />,
    protected: false
  },
  dashboard: {
    path: "/",
    name: "Αρχική",
    icon: "fa-home",
    element: <Dashboard />,
    protected: true
  },
  repairs: {
    path: "/repairs",
    name: "Επισκευές",
    icon: "fa-clipboard-list",
    element: <RepairsList />,
    protected: true
  },
  vehicles: {
    path: "/vehicles",
    name: "Νέα Επισκευή",
    icon: "fa-plus",
    element: <VehiclesSearch />,
    protected: true,
    expertHidden: true,
    adminHidden: true
  },
  repairDetails: {
    path: "/repairs/:repairId",
    name: "Επισκευή",
    icon: "fa-clipboard-list",
    element: <RepairDetails />,
    protected: true
  },
  usersSearch: {
    path: "/users",
    name: "Χρήστες",
    icon: "fa-users",
    element: <UsersSearch />,
    protected: true,
    adminOnly: true
  },
  garagesSearch: {
    path: "/garages",
    name: "Συνεργεία",
    icon: "fa-wrench",
    element: <GaragesSearch />,
    protected: true,
    adminOnly: true
  },
  garageDetails: {
    path: "/garages/:garageId",
    name: "Συνεργείο",
    element: <GarageDetails />,
    protected: true
  },
  userDetails: {
    path: "/users/:userId",
    name: "Χρήστης",
    element: <UserDetails />,
    protected: true,
    adminOnly: true
  },
  profile: {
    path: "/profile",
    name: "Προφίλ",
    element: <UserDetails />,
    protected: true
  },
  garageProfile: {
    path: "/garage-profile",
    name: "Το Συνεργείο μου",
    element: <GarageDetails />,
    protected: true
  }
}

// Generate routes for React Router
export const generateRoutes = (isExpertValue, isAdminValue) => {
  const notFoundRoute = {
    path: "*",
    element: (
      <div className="min-vh-100 d-flex justify-content-center align-items-center">
        <div className="text-center">
          <h1 className="display-1">404</h1>
          <p className="lead">Η σελίδα δε βρέθηκε</p>
          <a href="/" className="btn btn-primary">
            Επιστροφή στην αρχική
          </a>
        </div>
      </div>
    ),
    protected: false
  }

  // Process routes to add protection wrappers
  const processedRoutes = Object.values(routeConfig).map(route => {
    if (route.expertHidden && isExpertValue) {
      return null
    } else if (route.adminHidden && isAdminValue) {
      return null
    }

    if (!route.protected) {
      return route
    }

    // For admin routes
    if (route.adminOnly && isAdminValue) {
      return {
        ...route,
        element: <AdminRoute>{route.element}</AdminRoute>
      }
    }

    // For regular protected routes
    return {
      ...route,
      element: <ProtectedRoute>{route.element}</ProtectedRoute>
    }
  })

  return [...processedRoutes, notFoundRoute].filter(route => route !== null)
}

// Helper function to get route name by path
export const getRouteNameByPath = path => {
  // First try exact match
  const exactRoute = Object.values(routeConfig).find(route => route.path === path && route.element)
  if (exactRoute) return exactRoute.name

  // If no exact match, try to match dynamic routes
  const pathParts = path.split("/")
  const route = Object.values(routeConfig).find(route => {
    // Only consider routes that have an element
    if (!route.element) return false

    const routeParts = route.path.split("/")
    if (routeParts.length !== pathParts.length) return false

    return routeParts.every((part, i) => {
      if (part.startsWith(":")) return true
      return part === pathParts[i]
    })
  })

  if (!route) return "Unknown"

  // For dynamic routes, append the parameter value to the name
  const routeParts = route.path.split("/")
  const paramIndex = routeParts.findIndex(part => part.startsWith(":"))

  if (paramIndex !== -1) {
    return `${route.name} ${pathParts[paramIndex]}`
  }

  return route.name
}

// Helper function to get navigation routes for header
export const getNavigationRoutes = (isAdmin, isExpert) => {
  // Routes that should appear in navigation
  const navigationRouteKeys = ["dashboard", "repairs", "vehicles", "garagesSearch", "usersSearch"]

  return navigationRouteKeys
    .map(key => routeConfig[key])
    .filter(route => {
      if (!route) return false
      // Filter based on admin/expert visibility rules
      if (route.adminOnly && !isAdmin()) return false
      if (route.expertHidden && isExpert()) return false
      if (route.adminHidden && isAdmin()) return false
      return true
    })
    .map(route => ({
      path: route.path,
      name: route.name,
      icon: route.icon,
      adminOnly: route.adminOnly || false,
      expertHidden: route.expertHidden || false,
      adminHidden: route.adminHidden || false
    }))
}
